import { Direction } from '@angular/cdk/bidi';
import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { ServiceResponse } from 'src/app/@shared/interfaces/service-response.interface';
import { LanguageService } from 'src/app/@shared/services/language/language.service';
import { eCrowdednessType } from 'src/app/modules/route-planner/enums/route-planning/crowdedness-type.enum';
import { eSectionType } from 'src/app/modules/route-planner/enums/route-planning/section-type.enum';
import { eTransportMode } from 'src/app/modules/route-planner/enums/route-planning/transport-mode.enum';
import { RoutePlanning } from 'src/app/modules/route-planner/interfaces/route-planning/@route-planning.interface';
import { RouteCalendarAtStop } from 'src/app/modules/route-planner/interfaces/route-planning/gtfs/route-calendar-at-stop.interface';
import { TariffProfile } from 'src/app/modules/route-planner/interfaces/route-planning/gtfs/tariff-profile.interface';
import { NextStopTime } from 'src/app/modules/route-planner/interfaces/route-planning/next-stop-times.interface';
import { Section } from 'src/app/modules/route-planner/interfaces/route-planning/section.interface';
import { CalendarService } from 'src/app/modules/route-planner/services/calendar/calendar.service';
import {
  CROWDEDNESS_HEAVY,
  CROWDEDNESS_LIGHT,
  CROWDEDNESS_MEDIUM,
  CROWDEDNESS_UNKNOWN,
  ROUTE_DETAILS,
  ROUTE_TRIP_OPTION_ARIA_LABEL,
} from 'src/app/modules/route-planner/constants/multi-language.constants';
import { HourPipe } from 'src/app/modules/route-planner/pipes/hour.pipe';
import { RoutePlanningService } from 'src/app/modules/route-planner/services/route-planning/route-planning.service';

@Component({
    selector: 'app-source-destination-results-list-item',
    templateUrl: './source-destination-results-list-item.component.html',
    styleUrls: ['./source-destination-results-list-item.component.scss'],
    standalone: false
})
export class SourceDestinationResultsListItemComponent implements OnInit, OnDestroy {
  @Input() route: RoutePlanning;
  @Input() showRoutePrice: boolean;
  @Input() selectedProfile: TariffProfile;

  @Output() cardActivate = new EventEmitter<void>();
  @Output() cardHover = new EventEmitter<void>();

  firstTransitSection: Section
  hasSpecialData: boolean
  interval: NodeJS.Timeout
  timeText: string
  sectionIconName: string
  direction: Direction
  firstFlexibleBusSection: Section
  computedAgencyId: number

  constructor(
    private readonly routePlanningService: RoutePlanningService,
    private readonly calendarService: CalendarService,
    private readonly languageService: LanguageService
  ) { }

  ngOnInit(): void {
    this.firstTransitSection = this.route.sections.find((section: Section) => section.type === eSectionType.TRANSIT)
    this.hasSpecialData = this.route.specialData.isAccessible || this.route.specialData.isFlexible || this.route.specialData.isOrderInAdvanced || this.route.specialMessages.routeMessages.length > 0 || this.route.specialMessages.stopMessages.length > 0
    this.firstFlexibleBusSection = this.route.sections.find((section: Section) => section.type === eSectionType.TRANSIT &&
      (section.transport.mode === eTransportMode.BUS || section.transport.mode === eTransportMode.PRIVATE_BUS) &&
      section.specialData?.isFlexible)
    if (!this.route.isFutureSearch) {
      this.interval = setInterval(() => {
        this.getTimeStopsOnDeparture()
      }, 60000)
    }

    this.initializeMultiLanguageTexts()

    if (this.firstFlexibleBusSection?.agency?.id){
        if (this.firstFlexibleBusSection.agency.id.includes('_')) {
          this.computedAgencyId = +(this.firstFlexibleBusSection.agency.id.split('_')[0]);
        } else {
          this.computedAgencyId = +(this.firstFlexibleBusSection.agency.id);
        }
      } else if (this.firstTransitSection?.agency?.id) {
        if (this.firstTransitSection.agency.id.includes('_')) {
          this.computedAgencyId = +(this.firstTransitSection.agency.id.split('_')[0]);
        } else {
          this.computedAgencyId = +(this.firstTransitSection.agency.id);
        }
    }
  }

  ngOnDestroy(): void {
    clearInterval(this.interval)
  }

  calcTravelDuration(duration: number) {
    this.timeText = this.routePlanningService.calcTravelDuration(duration);
  }

  initializeMultiLanguageTexts() {
    this.languageService.selectedLanguage$.subscribe(language => {
      this.direction = language.direction
      this.sectionIconName = language.direction === "rtl" ? "arrow-left" : "arrow-right"
      this.calcTravelDuration(this.route.travelSummary.duration)
    })
  }

  getTimeStopsOnDeparture() {
    this.calendarService.GetRouteCalendarAtStopsByStopCodes({ stopCodes: [this.firstTransitSection.departure.place.code], routeDesc: this.firstTransitSection.transport.description })
      .subscribe({
        next: (response: ServiceResponse<{ [stopId: number]: RouteCalendarAtStop }>) => {
          const data = response.data
          if (data) {
            const departureCalendar = data[parseInt(this.firstTransitSection.departure.place.code)]
            if (departureCalendar) {
              this.firstTransitSection.departure = {
                ...this.firstTransitSection.departure,
                nextStopsTimes: [...departureCalendar.stopTimes]
              }
              if (this.firstTransitSection?.departure?.nextStopsTimes?.length) {
                this.firstTransitSection.departure.nextStopsTimes.forEach((stopTime: NextStopTime) => {
                  if (stopTime.isRealTime) {
                  }
                })
              }
            }
          }
        },
        error: () => clearInterval(this.interval)
      })

  }
  /** Screen reader + a11y tree: route option is a button with “route details” + visible summary. */
  get routeCardAriaLabel(): string {
    const langKey = this.languageService.getSelectedLanguage().language;
    const tpl =
      ROUTE_TRIP_OPTION_ARIA_LABEL[langKey] ??
      ROUTE_TRIP_OPTION_ARIA_LABEL['english'];
    const summary = this.buildRouteCardAriaSummary();
    if (!summary) {
      return ROUTE_DETAILS[langKey] ?? ROUTE_DETAILS['english'];
    }
    return tpl.replace('{0}', summary);
  }

  private buildRouteCardAriaSummary(): string {
    const ts = this.route?.travelSummary;
    if (!ts?.travelTime?.start) {
      return '';
    }
    const lang = this.languageService.getSelectedLanguage();
    const locale =
      lang.symbol === 'he'
        ? 'he-IL'
        : lang.symbol === 'ar'
          ? 'ar-IL'
          : 'en-GB';
    const start = new Date(ts.travelTime.start);
    const weekday = start.toLocaleDateString(locale, { weekday: 'short' });
    const dd = String(start.getDate()).padStart(2, '0');
    const mm = String(start.getMonth() + 1).padStart(2, '0');
    const datePart = `${weekday}, ${dd}.${mm}`;
    const hourPipe = new HourPipe();
    const sh = hourPipe.transform(ts.travelTime.startHour as any);
    const eh = hourPipe.transform(ts.travelTime.endHour as any);
    const timeRange =
      sh && eh ? `${sh}–${eh}` : sh || eh ? [sh, eh].filter(Boolean).join('–') : '';
    const parts = [datePart, timeRange, this.timeText].filter(
      (p) => !!p && String(p).trim().length > 0
    );
    return parts.join(', ');
  }

  onCardActivate(event: MouseEvent): void {
    const t = event.target as HTMLElement | null;
    if (t?.closest('button, a[href], input, select, textarea')) {
      return;
    }
    this.cardActivate.emit();
  }

  onCardKeyActivate(event: Event): void {
    event.preventDefault();
    this.cardActivate.emit();
  }

  onCardMouseEnter(): void {
    this.cardHover.emit();
  }

  getImageFromCrowdedness(
    crowdedness?: eCrowdednessType
  ): string | null {
    switch (crowdedness) {
      case eCrowdednessType.LIGHT:
        return 'crowdedness-light';
      case eCrowdednessType.MEDIUM:
        return 'crowdedness-medium';
      case eCrowdednessType.HEAVY:
        return 'crowdedness-heavy';
      default:
        return 'crowdedness-empty';
    }
  }

  get crowdednessIconSrc(): string {
    return `/assets/icons/${this.getImageFromCrowdedness(this.route.routeCrowdedness)}.svg`;
  }

  resolveCrowdednessAlt(): string {
    switch (this.getImageFromCrowdedness(this.route.routeCrowdedness)) {
      case 'crowdedness-light':
        return CROWDEDNESS_LIGHT[this.languageService.getSelectedLanguage().language];
      case 'crowdedness-medium':
        return CROWDEDNESS_MEDIUM[this.languageService.getSelectedLanguage().language];
      case 'crowdedness-heavy':
        return CROWDEDNESS_HEAVY[this.languageService.getSelectedLanguage().language];
      default:
        return CROWDEDNESS_UNKNOWN[this.languageService.getSelectedLanguage().language];
    }
  }
}
